import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Tabs, Tab } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { modifyConsentCookie } from './cookies';
import ResetButton from './ConsentActions/ResetButton';
import CookiesVendorsTab from './Tabs/CookiesVendorsTab';
import ConsentTabContainer from './Tabs/ConsentTabContainer';
import PurposesFeaturesTab from './Tabs/PurposesFeaturesTab';
import { tcfApi, CMP_EVENTS, useTCString } from './iab-tcf';

function a11yProps(index) {
  return {
    id: `consent-tab-${index}`,
    'aria-controls': `consent-tabpanel-${index}`,
  };
}

const useStyles = makeStyles(theme => ({
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  saveButton: {
    padding: theme.spacing(0, 4),
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s',
    height: 38,
    backgroundColor: theme.palette.common.white,
  },
}));

/**
 * Customize consent screen for:
 * - cookie categories IDs
 * - IAB vendor IDs (by cookie ID)
 * - IAB purpose / features
 */
function ConsentCustomizeScreen({ setActiveScreen, selectedCookies, setSelectedCookies, onClose }) {
  const classes = useStyles();
  const { refreshTCString } = useTCString();

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const savePreferences = () => {
    modifyConsentCookie(selectedCookies);
    refreshTCString();
    tcfApi.triggerCmpEvent(CMP_EVENTS.COMPLETE);

    const atLeastOneSelected = Object.values(selectedCookies).some(value => value);
    onClose({ consentGranted: atLeastOneSelected });
  };

  return (
    <Fragment>
      <Tabs value={activeTab} onChange={handleTabChange} aria-label="Customize Consent Tabs">
        <Tab label="Cookies and Vendors" {...a11yProps(0)} data-test-id="consent-cookie-vendor-tab" />
        <Tab label="Purposes and Features" {...a11yProps(1)} data-test-id="consent-purpose-feature-tab" />
      </Tabs>
      <ConsentTabContainer value={activeTab} index={0}>
        <CookiesVendorsTab selectedCookies={selectedCookies} setSelectedCookies={setSelectedCookies} />
      </ConsentTabContainer>
      <ConsentTabContainer value={activeTab} index={1}>
        <PurposesFeaturesTab />
      </ConsentTabContainer>
      <div className={classes.buttonContainer}>
        <ResetButton setActiveScreen={setActiveScreen} setSelectedCookies={setSelectedCookies} />
        <Button
          variant="outlined"
          className={classes.saveButton}
          data-test-id="consent-customize-save"
          onClick={savePreferences}
        >
          Save Preferences
        </Button>
      </div>
    </Fragment>
  );
}

ConsentCustomizeScreen.propTypes = {
  setActiveScreen: PropTypes.func.isRequired,
  selectedCookies: PropTypes.object.isRequired,
  setSelectedCookies: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ConsentCustomizeScreen;
